// Ubicación en el repo: src/components/HearingAidFormModal.jsx
// Modal Crear/Editar venta y calibración de audífono — mismo contenido
// que openHearingAidModal() del prototipo (línea ~3140): paciente, marca
// y modelo (desde catálogo, precio autocompletado desde ahí), oído,
// N° de serie (uno o dos según oído), fecha de venta, garantía, notas.
//
// Ver DECISIONS.md #29: si el modelo elegido es `power_type='Recargable'`
// (pack de 2 de fábrica), una venta Bilateral descuenta 1 unidad de stock
// (el pack) y usa un solo precio total — no 2 unidades con precio
// individual cada una, que es el comportamiento normal para modelos a pila.
import { useMemo, useRef, useState } from 'react';
import { createHearingaid, updateHearingaid } from '../lib/hearingaids';
import { searchPatients } from '../lib/patients';
import { formatPatientName } from '../lib/format';

const SIDES = ['Derecho', 'Izquierdo', 'Bilateral'];

// Traduce el error de Postgres por índice único duplicado (código 23505,
// migración 0044 — un audífono activo ya tiene cargado ese mismo número de
// serie) a un mensaje claro para quien está en el formulario, en vez del
// texto crudo de la base de datos. Devuelve null si el error no es este
// caso, para que el llamador siga con su mensaje genérico.
function duplicateSerialMessage(err) {
  if (err?.code !== '23505') return null;
  if (err.message?.includes('hearingaids_serial_od_unique')) {
    return 'Ya existe otro audífono activo con ese número de serie en el campo OD.';
  }
  if (err.message?.includes('hearingaids_serial_oi_unique')) {
    return 'Ya existe otro audífono activo con ese número de serie en el campo OI.';
  }
  if (err.message?.includes('hearingaids_serial_unique')) {
    return 'Ya existe otro audífono activo con ese número de serie.';
  }
  return 'Ya existe otro registro con ese mismo dato único.';
}

function initialForm(ha, catalog) {
  if (ha) {
    const match = catalog.find((c) => c.id === ha.catalog_id);
    return {
      patientId: ha.patient_id,
      patientLabel: ha.patients ? formatPatientName(ha.patients) : '',
      brand: ha.brand,
      catalogId: ha.catalog_id,
      side: ha.side,
      serial: ha.serial || '',
      price: ha.price ?? (match ? match.price : ''),
      serialOd: ha.serial_od || '',
      serialOi: ha.serial_oi || '',
      priceOd: ha.price_od ?? (match ? match.price : ''),
      priceOi: ha.price_oi ?? (match ? match.price : ''),
      date: ha.purchase_date,
      warrantyMonths: ha.warranty_months ?? 12,
      notes: ha.notes || '',
    };
  }
  return {
    patientId: null,
    patientLabel: '',
    brand: '',
    catalogId: '',
    side: 'Derecho',
    serial: '',
    price: '',
    serialOd: '',
    serialOi: '',
    priceOd: '',
    priceOi: '',
    date: new Date().toLocaleDateString('sv-SE'),
    warrantyMonths: 12,
    notes: '',
  };
}

export default function HearingAidFormModal({ hearingaid, branchId, catalog, actorId, onClose, onSaved }) {
  const isEdit = !!hearingaid;
  // categorySpecialRole (migración 0050, 25-ago-2026): ya no compara el
  // nombre/type exacto "Audífono" — HearingAids.jsx ya filtra el catálogo
  // que llega acá por `special_role`, esto es un segundo resguardo por si
  // algún día `catalog` llega sin filtrar desde otro lado. Ver
  // CategoryFormModal.jsx.
  const audifonos = useMemo(() => catalog.filter((c) => c.category?.special_role === 'audifono'), [catalog]);
  const brands = useMemo(() => [...new Set(audifonos.map((c) => c.brand))].sort(), [audifonos]);

  const [form, setForm] = useState(() => {
    const f = initialForm(hearingaid, catalog);
    if (!f.brand && brands.length) f.brand = brands[0];
    if (!f.catalogId) {
      const first = audifonos.find((c) => c.brand === f.brand);
      f.catalogId = first?.id || '';
      if (first && f.price === '') f.price = first.price;
      if (first && f.priceOd === '') f.priceOd = first.price;
      if (first && f.priceOi === '') f.priceOi = first.price;
    }
    return f;
  });
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Guarda de doble-envío: un ref (no un state) porque se lee/escribe de
  // forma síncrona apenas se hace click, sin esperar el re-render que
  // recién deshabilita el botón — evita crear 2 ventas (y descontar stock
  // 2 veces) si se hace doble clic en "Guardar" antes de que el botón
  // alcance a deshabilitarse.
  const submittingRef = useRef(false);

  const modelsForBrand = useMemo(() => audifonos.filter((c) => c.brand === form.brand), [audifonos, form.brand]);
  const selectedCatalog = audifonos.find((c) => c.id === form.catalogId);
  const isPack = selectedCatalog?.power_type === 'Recargable';

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function handleBrandChange(brand) {
    const first = audifonos.find((c) => c.brand === brand);
    setForm((f) => ({
      ...f,
      brand,
      catalogId: first?.id || '',
      price: first ? first.price : '',
      priceOd: first ? first.price : '',
      priceOi: first ? first.price : '',
    }));
  }

  function handleModelChange(catalogId) {
    const c = audifonos.find((x) => x.id === catalogId);
    setForm((f) => ({
      ...f,
      catalogId,
      price: c ? c.price : f.price,
      priceOd: c ? c.price : f.priceOd,
      priceOi: c ? c.price : f.priceOi,
    }));
  }

  function selectPatient(p) {
    set('patientId', p.id);
    setForm((f) => ({ ...f, patientLabel: formatPatientName(p) }));
    setPatientQuery('');
    setPatientResults([]);
  }

  async function handlePatientSearch(value) {
    setPatientQuery(value);
    if (!value.trim()) {
      setPatientResults([]);
      return;
    }
    try {
      setPatientResults(await searchPatients(value));
    } catch {
      setPatientResults([]);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current) return;
    setError('');
    if (!form.patientId) {
      setError('Selecciona un paciente.');
      return;
    }
    if (!selectedCatalog) {
      setError('Selecciona un modelo válido del catálogo.');
      return;
    }
    if (!form.date) {
      setError('Indica la fecha de venta.');
      return;
    }
    const unitsNeeded = form.side === 'Bilateral' ? (isPack ? 1 : 2) : 1;
    if (!isEdit && Number(selectedCatalog.stock || 0) < unitsNeeded) {
      const unitLabel = isPack ? 'pack(s)' : 'unidad(es)';
      const ok = window.confirm(
        `Solo quedan ${Number(selectedCatalog.stock || 0)} ${unitLabel} de ${selectedCatalog.brand} ${selectedCatalog.model} en inventario y esta venta necesita ${unitsNeeded}. ¿Registrar la venta de todas formas?`
      );
      if (!ok) return;
    }

    submittingRef.current = true;
    setSaving(true);
    try {
      const values = {
        patient_id: form.patientId,
        branch_id: branchId,
        catalog_id: selectedCatalog.id,
        brand: selectedCatalog.brand,
        model: selectedCatalog.model,
        side: form.side,
        units_used: unitsNeeded,
        purchase_date: form.date,
        warranty_months: Number(form.warrantyMonths) || 0,
        notes: form.notes.trim() || null,
        serial: form.side === 'Bilateral' ? null : form.serial.trim().toUpperCase() || null,
        price: form.side === 'Bilateral' ? null : form.price !== '' ? Number(form.price) : null,
        serial_od: form.side === 'Bilateral' ? form.serialOd.trim().toUpperCase() || null : null,
        serial_oi: form.side === 'Bilateral' ? form.serialOi.trim().toUpperCase() || null : null,
        // Pack: un solo precio total, guardado en price_od (price_oi queda
        // null para que sumar price_od+price_oi siga dando el total
        // correcto de la venta en cualquier reporte futuro).
        price_od: form.side === 'Bilateral' ? (form.priceOd !== '' ? Number(form.priceOd) : null) : null,
        price_oi: form.side === 'Bilateral' && !isPack ? (form.priceOi !== '' ? Number(form.priceOi) : null) : null,
      };
      if (isEdit) {
        await updateHearingaid(hearingaid.id, values, actorId);
      } else {
        await createHearingaid(values, actorId);
      }
      onSaved();
    } catch (err) {
      setError(duplicateSerialMessage(err) || err.message || 'No se pudo guardar la venta.');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar venta' : 'Nueva venta y calibración'}</h2>

        {audifonos.length === 0 ? (
          <>
            <p className="field-hint">
              Antes de registrar una venta, agrega al menos una marca y modelo en <strong>Catálogo</strong>.
            </p>
            <div className="modal-actions">
              <button type="button" className="button-secondary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="patient-form">
            <div className="field">
              <span>Paciente *</span>
              {form.patientId ? (
                <div className="patient-picker-selected">
                  {form.patientLabel}
                  {!isEdit && (
                    <button type="button" className="link-button" onClick={() => set('patientId', null)}>
                      Cambiar
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <input
                    value={patientQuery}
                    onChange={(e) => handlePatientSearch(e.target.value)}
                    placeholder="Buscar por nombre o RUT…"
                  />
                  {patientResults.length > 0 && (
                    <ul className="patient-picker-results">
                      {patientResults.map((p) => (
                        <li key={p.id}>
                          <button type="button" onClick={() => selectPatient(p)}>
                            {formatPatientName(p)} {p.rut ? `— ${p.rut}` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="form-row">
              <label className="field">
                <span>Marca</span>
                <select value={form.brand} onChange={(e) => handleBrandChange(e.target.value)}>
                  {brands.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Modelo</span>
                <select value={form.catalogId} onChange={(e) => handleModelChange(e.target.value)}>
                  {modelsForBrand.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.model} ({Number(c.stock || 0)} en stock{c.power_type === 'Recargable' ? ', packs' : ''})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="field">
              <span>Oído</span>
              <select value={form.side} onChange={(e) => set('side', e.target.value)}>
                {SIDES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            {form.side === 'Bilateral' ? (
              <>
                {isPack && (
                  <p className="field-hint">
                    📦 Este modelo se vende en pack de 2 — esta venta va a descontar 1 unidad de stock (el pack
                    completo) y va a usar un solo precio total, no precio por audífono.
                  </p>
                )}
                <div className="form-row">
                  <label className="field">
                    <span>N° de serie · OD</span>
                    <input
                      value={form.serialOd}
                      onChange={(e) => set('serialOd', e.target.value)}
                      placeholder="Escanear código de barras o escribir…"
                    />
                  </label>
                  <label className="field">
                    <span>N° de serie · OI</span>
                    <input
                      value={form.serialOi}
                      onChange={(e) => set('serialOi', e.target.value)}
                      placeholder="Escanear código de barras o escribir…"
                    />
                  </label>
                </div>
                {isPack ? (
                  <label className="field">
                    <span>Precio del pack (CLP) — desde inventario</span>
                    <input type="number" value={form.priceOd} readOnly disabled style={{ background: 'var(--surface-alt)', maxWidth: 220 }} />
                  </label>
                ) : (
                  <div className="form-row">
                    <label className="field">
                      <span>Precio OD (CLP) — desde inventario</span>
                      <input type="number" value={form.priceOd} readOnly disabled style={{ background: 'var(--surface-alt)' }} />
                    </label>
                    <label className="field">
                      <span>Precio OI (CLP) — desde inventario</span>
                      <input type="number" value={form.priceOi} readOnly disabled style={{ background: 'var(--surface-alt)' }} />
                    </label>
                  </div>
                )}
              </>
            ) : (
              <div className="form-row">
                <label className="field">
                  <span>N° de serie</span>
                  <input
                    value={form.serial}
                    onChange={(e) => set('serial', e.target.value)}
                    placeholder="Escanear código de barras o escribir…"
                  />
                </label>
                <label className="field">
                  <span>Precio (CLP) — desde inventario</span>
                  <input type="number" value={form.price} readOnly disabled style={{ background: 'var(--surface-alt)' }} />
                </label>
              </div>
            )}

            <div className="form-row">
              <label className="field">
                <span>Fecha de venta *</span>
                <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
              </label>
              <label className="field">
                <span>Garantía (meses)</span>
                <input type="number" value={form.warrantyMonths} onChange={(e) => set('warrantyMonths', e.target.value)} />
              </label>
            </div>

            <label className="field">
              <span>Notas</span>
              <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Adaptación, garantía, observaciones…" />
            </label>

            {error && <p className="auth-error">{error}</p>}

            <div className="modal-actions">
              <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="button-primary" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
